import { call } from '@advanza/api'
import { useAppSelector } from 'hooks/hooks'
import { useCallback, useEffect, useState } from 'react'

/** Mirrors Api\Model\Entity\SepaBatch (xml is hidden in list payloads). */
export interface SepaBatch {
    sepa_batch_id: number
    message_id: string
    country_code: string
    filename: string
    invoice_count: number
    /** Computed server-side; invoice rows load separately on expand. */
    fulfilled_count: number
    total_amount: number | string
    created_by: number | null
    created: string
    created_by_user?: { id: number; username: string } | null
}

/** Invoice row as contained in the SEPA batch endpoints. */
export interface SepaBatchInvoice {
    invoice_id: number
    invoice_nr: string
    datetime: string
    amount_netto: number
    transaction_status: string | null
    service_provider_id: number
    provider?: {
        service_provider_id: number
        business_name: string
        country_code: string
    } | null
}

export interface EligibleCreditInvoice extends SepaBatchInvoice {
    /** False when IBAN or account holder is missing — would fail that payout. */
    has_bank_details: boolean
}

/** Invoice that was selected but did not make it into the generated file. */
export interface SkippedInvoice {
    invoice_id: number
    invoice_nr: string
    reason: string
}

/** Keeps the per-invoice side effects of the fulfil endpoint within one request's budget. */
const FULFIL_CHUNK_SIZE = 200

function getErrorMessage(rejection: unknown): string {
    if (typeof rejection === 'object' && rejection !== null && 'error' in rejection) {
        return String((rejection as { error: unknown }).error)
    }

    return 'request failed'
}

/** Hand the generated/stored XML to the browser as a file download. */
function saveXmlFile(filename: string, xml: string): void {
    const blob = new Blob([xml], { type: 'application/xml' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename || 'credit-invoices-sepa.xml'
    link.click()
    // Revoking synchronously can race the download in some browsers.
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

/**
 * Batch list for the overview tab, plus the batch-level actions
 * (re-download, set invoices to fulfilled, release bounced payments).
 * Invoice rows are loaded per batch via fetchBatchInvoices when a card
 * expands, keeping the list payload small.
 *
 * @see SepaBatchesController::getList()
 * @see SepaBatchesController::getInvoices()
 * @see SepaBatchesController::download()
 * @see SepaBatchesController::release()
 * @see InvoicesController::setTransactionFulfilled()
 */
export function useSepaBatches() {
    const { countries } = useAppSelector((state) => state.countries)
    const [batches, setBatches] = useState<SepaBatch[]>([])
    const [legacyInvoices, setLegacyInvoices] = useState<{
        count: number
        invoices: SepaBatchInvoice[]
    }>({ count: 0, invoices: [] })
    const [isFetching, setIsFetching] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const fetchBatches = useCallback(async () => {
        setIsFetching(true)
        setError(null)
        try {
            const response = await call('office/sepa-batches/get-list', {
                query: { countries: countries.join(',') },
            })
            setBatches(response.batches)
            setLegacyInvoices(response.legacyInvoices)
        } catch (rejection) {
            setError(getErrorMessage(rejection))
        } finally {
            setIsFetching(false)
        }
    }, [countries])

    useEffect(() => {
        fetchBatches()
    }, [fetchBatches])

    const fetchBatchInvoices = async (batchId: number): Promise<SepaBatchInvoice[]> => {
        try {
            const response = await call(`office/sepa-batches/get-invoices/${batchId}`)
            return response.invoices
        } catch (rejection) {
            setError(getErrorMessage(rejection))
            return []
        }
    }

    const downloadBatch = async (batchId: number) => {
        try {
            const response = await call(`office/sepa-batches/download/${batchId}`)
            saveXmlFile(response.filename, response.xml)
        } catch (rejection) {
            setError(getErrorMessage(rejection))
        }
    }

    /**
     * Reuses the bulk action of the invoices page — same semantics per
     * invoice. Chunked because the endpoint runs follow-up side effects per
     * invoice and a single huge request could hit the PHP time limit.
     */
    const setInvoicesFulfilled = async (invoiceIds: number[]) => {
        try {
            for (let start = 0; start < invoiceIds.length; start += FULFIL_CHUNK_SIZE) {
                await call('office/invoices/set-transaction-fulfilled', {
                    method: 'post',
                    payload: { ids: invoiceIds.slice(start, start + FULFIL_CHUNK_SIZE) },
                })
            }
        } catch (rejection) {
            setError(getErrorMessage(rejection))
        }
        await fetchBatches()
    }

    /**
     * For payments the bank rejected: the invoices go back to the creation
     * tab (status and batch link reset) so they can go into a new file.
     */
    const releaseInvoices = async (batchId: number, invoiceIds: number[]) => {
        try {
            await call('office/sepa-batches/release', {
                method: 'post',
                payload: { sepaBatchId: batchId, invoiceIds },
            })
        } catch (rejection) {
            setError(getErrorMessage(rejection))
        }
        await fetchBatches()
    }

    return {
        batches,
        legacyInvoices,
        isFetching,
        error,
        refresh: fetchBatches,
        fetchBatchInvoices,
        downloadBatch,
        setInvoicesFulfilled,
        releaseInvoices,
    }
}

/**
 * Eligible credit invoices + batch creation for the creation tab.
 * One SEPA file per country, so the list is scoped to a single country.
 *
 * @see SepaBatchesController::getEligibleInvoices()
 * @see SepaBatchesController::create()
 */
export function useSepaCreation(countryCode: string | undefined) {
    const [invoices, setInvoices] = useState<EligibleCreditInvoice[]>([])
    const [count, setCount] = useState(0)
    const [totalAmount, setTotalAmount] = useState(0)
    const [skippedInvoices, setSkippedInvoices] = useState<SkippedInvoice[]>([])
    const [isFetching, setIsFetching] = useState(false)
    const [isCreating, setIsCreating] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const fetchInvoices = useCallback(async () => {
        if (!countryCode) {
            setInvoices([])
            setCount(0)
            setTotalAmount(0)
            return
        }
        setIsFetching(true)
        setError(null)
        try {
            const response = await call(`office/sepa-batches/get-eligible-invoices/${countryCode}`)
            setInvoices(response.invoices)
            setCount(response.count)
            setTotalAmount(response.totalAmount)
        } catch (rejection) {
            setError(getErrorMessage(rejection))
        } finally {
            setIsFetching(false)
        }
    }, [countryCode])

    useEffect(() => {
        fetchInvoices()
    }, [fetchInvoices])

    /** Creates the batch and immediately hands the XML to the browser. */
    const createBatch = async (invoiceIds: number[]): Promise<boolean> => {
        if (!countryCode || isCreating) {
            return false
        }
        setIsCreating(true)
        setError(null)
        setSkippedInvoices([])
        try {
            const response = await call('office/sepa-batches/create', {
                method: 'post',
                payload: { countryCode, invoiceIds },
            })
            saveXmlFile(response.filename, response.xml)
            setSkippedInvoices(response.skippedInvoices || [])
            return true
        } catch (rejection) {
            setError(getErrorMessage(rejection))
            return false
        } finally {
            setIsCreating(false)
            await fetchInvoices()
        }
    }

    return {
        invoices,
        count,
        totalAmount,
        skippedInvoices,
        isFetching,
        isCreating,
        error,
        refresh: fetchInvoices,
        createBatch,
    }
}
